import type { WavetableRuntimeTable } from "./types";
import { WAVETABLE_FRAME_SIZE, WAVETABLE_MAX_FRAMES } from "./types";

export type ParsedSerumWav = {
  sampleRate: number;
  frameSize: typeof WAVETABLE_FRAME_SIZE;
  frameCount: number;
  samples: Float32Array;
};

function readChunk(
  view: DataView,
  offset: number,
): { id: string; size: number; dataOffset: number; next: number } | null {
  if (offset + 8 > view.byteLength) {
    return null;
  }
  const id = String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  );
  const size = view.getUint32(offset + 4, true);
  const dataOffset = offset + 8;
  const next = dataOffset + size + (size & 1);
  return { id, size, dataOffset, next };
}

/**
 * Parse a Serum-style wavetable WAV: mono, frames of 2048 samples.
 * Supports PCM int32 (fmt 1, KRC) and IEEE float32 (fmt 3, BVKER).
 */
export function parseSerumWav(bytes: ArrayBuffer | Uint8Array): ParsedSerumWav {
  const buffer = bytes instanceof ArrayBuffer ? bytes : bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
  const view = new DataView(buffer);
  if (view.byteLength < 12) {
    throw new Error("WAV too short");
  }
  const riff = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
  const wave = String.fromCharCode(view.getUint8(8), view.getUint8(9), view.getUint8(10), view.getUint8(11));
  if (riff !== "RIFF" || wave !== "WAVE") {
    throw new Error("Not a RIFF/WAVE file");
  }

  let audioFormat = 0;
  let channels = 0;
  let sampleRate = 44100;
  let bitsPerSample = 0;
  let blockAlign = 0;
  let dataOffset = -1;
  let dataSize = 0;

  let offset = 12;
  while (offset + 8 <= view.byteLength) {
    const chunk = readChunk(view, offset);
    if (!chunk) {
      break;
    }
    if (chunk.id === "fmt " && chunk.size >= 16) {
      audioFormat = view.getUint16(chunk.dataOffset, true);
      channels = view.getUint16(chunk.dataOffset + 2, true);
      sampleRate = view.getUint32(chunk.dataOffset + 4, true);
      blockAlign = view.getUint16(chunk.dataOffset + 12, true);
      bitsPerSample = view.getUint16(chunk.dataOffset + 14, true);
    } else if (chunk.id === "data") {
      dataOffset = chunk.dataOffset;
      dataSize = chunk.size;
    }
    offset = chunk.next;
  }

  if (dataOffset < 0 || dataSize <= 0) {
    throw new Error("WAV missing data chunk");
  }
  if (channels !== 1) {
    throw new Error(`Expected mono wavetable, got ${channels} channels`);
  }
  if (bitsPerSample !== 32 || blockAlign !== 4) {
    throw new Error(`Expected 32-bit samples, got ${bitsPerSample}-bit / align ${blockAlign}`);
  }
  if (audioFormat !== 1 && audioFormat !== 3) {
    throw new Error(`Unsupported WAV format ${audioFormat} (need PCM or float)`);
  }

  const sampleCount = Math.floor(dataSize / 4);
  if (sampleCount % WAVETABLE_FRAME_SIZE !== 0) {
    throw new Error(
      `Sample count ${sampleCount} is not a multiple of ${WAVETABLE_FRAME_SIZE}`,
    );
  }
  const frameCount = sampleCount / WAVETABLE_FRAME_SIZE;
  if (frameCount < 1 || frameCount > WAVETABLE_MAX_FRAMES) {
    throw new Error(`Frame count ${frameCount} out of range 1..${WAVETABLE_MAX_FRAMES}`);
  }

  const samples = new Float32Array(sampleCount);
  if (audioFormat === 3) {
    for (let i = 0; i < sampleCount; i++) {
      samples[i] = view.getFloat32(dataOffset + i * 4, true);
    }
  } else {
    const scale = 1 / 2147483648;
    for (let i = 0; i < sampleCount; i++) {
      samples[i] = view.getInt32(dataOffset + i * 4, true) * scale;
    }
  }

  return {
    sampleRate,
    frameSize: WAVETABLE_FRAME_SIZE,
    frameCount,
    samples,
  };
}

export function parsedToRuntimeTable(id: string, parsed: ParsedSerumWav): WavetableRuntimeTable {
  return {
    id,
    frameSize: WAVETABLE_FRAME_SIZE,
    frameCount: parsed.frameCount,
    samples: parsed.samples,
  };
}
